import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useMyContext } from '../components/Provider/CommonProvider';
import { getOptimizedChunkSize, getOptimizationInfo } from '../Utils/chunkSizeUtils';

function FileUploader() {
  const { fetchRequest } = useMyContext()
  const baseURL = process.env.REACT_APP_Baseurl;
  const [files, setFiles] = useState([]);
  const [fileStatusMap, setFileStatusMap] = useState([]);
  const abortControllers = useRef({});
  const fileStatusRef = useRef({});

  const updateFileStatus = (fileId, updates) => {
    setFileStatusMap(prev =>
      prev.map(item =>
        item.id === fileId ? { ...item, ...updates } : item
      )
    );
    fileStatusRef.current[fileId] = {
      ...(fileStatusRef.current[fileId] || {}),
      ...updates,
    };
  };

  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec > 1024 * 1024) return `${(bytesPerSec / (1024 * 1024)).toFixed(2)} MB/s`;
    if (bytesPerSec > 1024) return `${(bytesPerSec / 1024).toFixed(2)} KB/s`;
    return `${bytesPerSec.toFixed(2)} B/s`;
  };

  const uploadFile = async (file) => {
    const fileId = `${file.name}-${file.size}`;
    const CHUNK_SIZE = getOptimizedChunkSize(file.size);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Log optimization info using shared utility
    const optimizationInfo = getOptimizationInfo(file.size, CHUNK_SIZE);
    console.log(optimizationInfo.logMessage);

    updateFileStatus(fileId, { status: 'uploading', error: '', speed: '0 B/s' });

    const { resumeChunk: uploadedChunks = [] } = await fetchRequest({
      method: 'GET',
      url: `${baseURL}/admin/library/status?fileId=${fileId}`,
      data: {},
    })

    let uploaded = uploadedChunks?.length;

    for (let i = 0; i < totalChunks; i++) {
      if (uploadedChunks.includes(i)) continue;
      if (fileStatusRef.current[fileId]?.status === 'cancelled') return;

      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('fileId', fileId);
      formData.append('chunkIndex', i);
      formData.append('totalChunks', totalChunks);
      formData.append('fileName', file?.name);
      formData.append('fileType', file?.type);

      const controller = new AbortController();
      abortControllers.current[fileId] = controller;

      try {
        const startTime = Date.now();
        await fetchRequest({
          method: 'POST',
          url: `${baseURL}/admin/library/upload?upload_type=video`,
          data: formData,
          signal: controller.signal
        })

        const endTime = Date.now();

        const timeTaken = (endTime - startTime) / 1000;
        const speed = chunk.size / timeTaken;

        uploaded++;
        const progressPercent = Math.round((uploaded / totalChunks) * 100);
        updateFileStatus(fileId, {
          progress: progressPercent,
          speed: formatSpeed(speed),
        });

        if (uploaded === totalChunks) {
          updateFileStatus(fileId, { status: 'done', speed: '0 B/s' });
        }
      } catch (err) {
        if (err.name !== 'CanceledError') {
          updateFileStatus(fileId, { status: 'cancelled', error: "" });
        }
        break;
      }
    }
  };

  const handleFileDrop = (droppedFiles) => {
    const newFiles = Array.from(droppedFiles);
    const combined = [...files, ...newFiles];
    setFiles(combined);

    newFiles.forEach(file => {
      const fileId = `${file.name}-${file.size}`;
      setFileStatusMap(prev => [
        ...prev,
        { id: fileId, status: 'idle', error: '', speed: '0 B/s' }
      ]);
      uploadFile(file);
    });
  };

  const cancelUpload = (file) => {
    const fileId = `${file.name}-${file.size}`;
    abortControllers.current[fileId]?.abort();
    updateFileStatus(fileId, { status: 'cancelled', speed: '0 B/s' });
  };

  const retryUpload = (file) => {
    const fileId = `${file.name}-${file.size}`;
    updateFileStatus(fileId, { status: 'uploading', error: '', speed: '0 B/s' });
    uploadFile(file);
  };

  const removeFile = async (file) => {
    const fileId = `${file.name}-${file.size}`;
    await fetchRequest({
      method: 'DELETE',
      url: `${baseURL}/admin/library/cancel?fileId=${fileId}`,
      data: {},
    })
    setFiles(prev => prev.filter(f => `${f.name}-${f.size}` !== fileId));
    setFileStatusMap(prev => prev.filter(item => item.id !== fileId));
  };

  const fileInputRef = useRef();

  const handleClick = () => fileInputRef.current?.click();

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileDrop(droppedFiles);
    }
  };

  return (
    <div style={{ width: "500px", margin: "20px auto" }}>
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: "2px dashed #aaa",
          padding: "40px",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: "10px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={e => handleFileDrop(e.target.files)}
          style={{ display: 'none' }}
        />
        <p>Drag & Drop files here or <strong>click to browse</strong></p>
      </div>

      {files.map(file => {
        const fileId = `${file.name}-${file.size}`;
        const fileStatus = fileStatusMap.find(item => item.id === fileId) || {};
        const { progress = 0, status = 'idle', error = '', speed = '' } = fileStatus;

        return (
          <div key={fileId} style={{ marginTop: 20 }}>
            <strong>{file.name}</strong>
            <div style={{
              width: "100%",
              height: "20px",
              backgroundColor: "#eee",
              borderRadius: "5px",
              overflow: "hidden",
              marginTop: "5px"
            }}>
              <div style={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: "#2196f3",
                transition: "width 0.3s"
              }} />
            </div>
            <p style={{ margin: '5px 0' }}>
              {progress}% - {status}
              {speed && status === 'uploading' && ` - Speed: ${speed}`}
              {error && ` - Error: ${error}`}
            </p>
            {status === 'uploading' && (
              <button onClick={() => cancelUpload(file)}>Stop</button>
            )}
            {status === 'cancelled' && (
              <button onClick={() => retryUpload(file)}>Retry</button>
            )}
            {(status === 'done' || status === 'cancelled') && (
              <button onClick={() => removeFile(file)}>Remove</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FileUploader;